import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function PreviewPanel({
  qrUrl,
  logo,
  design,
  score,
  format,
  setFormat,
  onDownload,
  onCopy
}) {
  const [previewMode, setPreviewMode] = useState('flat');
  const radius = Number(design.radius) || 0;
  const stageStyle = {
    '--frame-area': design.frameColor || design.background,
    '--frame-accent': design.frameAccent || design.accent
  };
  const qrPaperStyle = { '--qr-radius': `${radius}%` };
  const ctaStyle = {
    backgroundColor: design.frameBackground || design.accent,
    color: design.frameTextColor || '#ffffff',
    borderColor: design.frameAccent || design.accent
  };
  const paperClass = [
    'qr-paper',
    `qr-shape-${design.shape}`,
    `qr-eyes-${design.eyes}`,
    design.transparentBackground ? 'qr-transparent' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={`preview-panel preview-${previewMode}`}>
      <div className="preview-toolbar">
        <span>LIVE PREVIEW</span>
        <div>
          <button
            className={previewMode === 'flat' ? 'active' : ''}
            onClick={() => setPreviewMode('flat')}
            aria-label="Flat preview"
            type="button"
          >
            <SafeIcon icon={FiIcons.FiMaximize2} />
          </button>
          <button
            className={previewMode === 'depth' ? 'active' : ''}
            onClick={() => setPreviewMode('depth')}
            aria-label="3D preview"
            type="button"
          >
            <SafeIcon icon={FiIcons.FiBox} />
          </button>
          <button
            className={previewMode === 'mobile' ? 'active' : ''}
            onClick={() => setPreviewMode('mobile')}
            aria-label="Mobile preview"
            type="button"
          >
            <SafeIcon icon={FiIcons.FiSmartphone} />
          </button>
        </div>
      </div>
      <div
        className={`preview-stage frame-${design.frame}`}
        style={stageStyle}
      >
        <div className={paperClass} style={qrPaperStyle}>
          {qrUrl ? (
            <img src={qrUrl} alt="Live generated QR code" />
          ) : (
            <div className="qr-loading">Generating…</div>
          )}
          {logo && <img className="qr-logo" src={logo} alt="Logo overlay" />}
        </div>
        {design.frame !== 'none' && (
          <span className="frame-cta" style={ctaStyle}>
            {design.frameText || 'Scan me'}
          </span>
        )}
        <span className="scan-label">
          <SafeIcon icon={FiIcons.FiCamera} />
          Point your camera to test
        </span>
      </div>
      <div className="score-card">
        <div
          className="score-ring"
          style={{ '--score': `${score * 3.6}deg` }}
        >
          <span>{score}</span>
        </div>
        <div>
          <h3>{score > 80 ? 'Excellent scannability' : 'Needs contrast'}</h3>
          <p>High contrast · Optimal density · Safe quiet zone</p>
        </div>
        <SafeIcon icon={FiIcons.FiCheckCircle} className="score-check" />
      </div>
      <div className="export-card">
        <div className="export-heading">
          <div>
            <h3>Ready to share</h3>
            <p>Export at any resolution</p>
          </div>
          <button aria-label="Export settings" type="button">
            <SafeIcon icon={FiIcons.FiSliders} />
          </button>
        </div>
        <div className="format-row">
          {['PNG', 'SVG', 'JPG'].map((item) => (
            <button
              className={format === item ? 'active' : ''}
              key={item}
              onClick={() => setFormat(item)}
              type="button"
            >
              {item}
            </button>
          ))}
        </div>
        <button className="download-button" onClick={onDownload} type="button">
          <SafeIcon icon={FiIcons.FiDownload} />
          Download {format}
          <span>2048 × 2048</span>
        </button>
        <div className="secondary-actions">
          <button onClick={onCopy} type="button">
            <SafeIcon icon={FiIcons.FiCopy} />
            Copy
          </button>
          <button type="button">
            <SafeIcon icon={FiIcons.FiCode} />
            Embed
          </button>
          <button type="button" aria-label="More export actions">
            <SafeIcon icon={FiIcons.FiMoreHorizontal} />
          </button>
        </div>
      </div>
      <p className="privacy-note">
        <SafeIcon icon={FiIcons.FiShield} />
        Generated privately in your browser
      </p>
    </aside>
  );
}

export default PreviewPanel;